import React, { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { BookOpen, Clock, ArrowRight, Search, Tag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import CM50Image from "../../assets/images/CM50.jpg";
import CM50GlobalImage from "../../assets/images/harsh-cm-50.jpg";
import CMFelicitationImage from "../../assets/images/harsh-cm-amreli.jpg";
import NCUIImage from "../../assets/images/NCUI Harsh.jpg";
import RUCImage from "../../assets/images/blog/visittorussia/DSC03649.JPG";

const Blog = () => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const blogPosts = [
    {
      title: "Rais of Tatarstan Visits Russian University of Cooperation and Launches RUC–Infotech Technopark",
      excerpt: "I had the privilege of participating as a special guest at the International Forum on Consumer Cooperation, where global leaders discussed the integration of modern technologies into the cooperative sector",
      readTime: "10 min read",
      category: "Global Cooperation",
      date: "March 2026",
      link: "/articles/blog/ruc-visit",
      image: RUCImage
    },
    {
      title: "Young Indian Cooperator Harsh Shines at CM50 Global Conference",
      excerpt: "I had the honour of representing the voice of global youth at the CM50 – Cooperative Ministers Conference held in Doha, where global leaders adopted 'A Contract for a New Global Economy'",
      readTime: "8 min read",
      category: "Global Leadership",
      date: "November 2025",
      link: "/articles/blog/cm50-global",
      image: CM50GlobalImage
    },
    {
      title: "CM Felicitates Me as ICA Youth President at Amreli",
      excerpt: "After my election in the International Cooperative Alliance (ICA) Youth Committee, the Honourable Chief Minister of Gujarat, Bhupendra Patel, felicitated me at an event in Amreli",
      readTime: "3 min read",
      category: "Recognition",
      date: "September 2025",
      link: "/articles/blog/cm-felicitation",
      image: CMFelicitationImage
    },
    {
      title: "CM50: A Contract for a New Global Economy",
      excerpt: "Representing global youth at the historic CM50 conference where cooperatives were positioned as the Third Way of Economy",
      readTime: "6 min read",
      category: "Global Leadership",
      date: "March 2025",
      link: "/articles/blog/cm50",
      image: CM50Image
    },
    {
      title: "NCUI Felicitates Me as ICA Youth Committee President",
      excerpt: "During the Annual General Meeting of NCUI held in New Delhi, I was graciously felicitated for being elected as the President of the ICA Youth Committee",
      readTime: "4 min read",
      category: "Recognition",
      date: "December 2024",
      link: "/articles/blog/ncui-felicitation",
      image: NCUIImage
    }
  ];

  const categories = ["All", ...new Set(blogPosts.map(post => post.category))];

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "All" || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-[#87212e] via-red-900 to-red-950 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="inline-block mb-6"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-white/20 rounded-2xl blur-xl" />
                <BookOpen className="w-20 h-20 text-white relative" />
              </div>
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="text-6xl md:text-7xl font-bold text-white mb-6 tracking-tight"
            >
              Blog
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="text-xl md:text-2xl text-red-100 max-w-3xl mx-auto mb-12 leading-relaxed"
            >
              Insights on leadership, innovation, and global cooperation
            </motion.p>
            
            {/* Search Bar */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="max-w-2xl mx-auto"
            >
              <div className="relative group">
                <div className="absolute inset-0 bg-white/20 rounded-2xl blur-xl group-hover:bg-white/30 transition-all duration-300" />
                <div className="relative bg-white/10 backdrop-blur-md rounded-2xl border border-white/30 shadow-2xl">
                  <Search className="absolute left-5 top-1/2 transform -translate-y-1/2 text-white/70 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search articles..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-14 pr-6 py-4 rounded-2xl bg-transparent text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all"
                  />
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-3 overflow-x-auto">
            <Tag className="w-5 h-5 text-gray-500 flex-shrink-0" />
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  selectedCategory === category
                    ? "bg-red-600 text-white shadow-md"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section ref={ref} className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">No articles found matching your criteria.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-8">
              {filteredPosts.map((post, index) => (
                <motion.article
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  onClick={() => post.link && navigate(post.link)}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer group"
                >
                  {post.image && (
                    <div className="h-48 overflow-hidden bg-gray-200">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="px-3 py-1 bg-red-100 text-red-600 rounded-full text-xs font-medium">
                        {post.category}
                      </span>
                      <span className="text-gray-400 text-xs">{post.date}</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-red-600 transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                    <p className="text-sm text-gray-500 mb-4">- Harsh M. Sanghani</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-gray-500 text-sm">
                        <Clock className="w-4 h-4 mr-1" />
                        {post.readTime}
                      </div>
                      <div className="inline-flex items-center text-red-600 font-medium group-hover:gap-2 transition-all">
                        Read More
                        <ArrowRight className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
